// VotingPower — small widget showing the connected wallet's R₿L voting power.
// Must be rendered INSIDE a WagmiProvider (GovernancePage provides it).
import React from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { Wallet, Vote, Loader2 } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tGov } from "../../i18n/governance";

const fmt = (n) => Number(n).toLocaleString();

export const VotingPower = ({ power, loadingPower }) => {
  const { lang } = useLanguage();
  const t = (k) => tGov(lang, k);
  const { address, isConnected } = useAccount();
  const { connectors, connect, isPending } = useConnect();
  const { disconnect } = useDisconnect();

  if (!isConnected) {
    return (
      <div className="rounded-2xl border border-purple-400/30 bg-purple-400/[0.05] p-5" data-testid="gov-connect-panel">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300/80">
          <Vote className="mr-1.5 inline h-3.5 w-3.5" /> {t("connectToVote")}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {connectors.map((c) => (
            <button key={c.uid} disabled={isPending} onClick={() => connect({ connector: c })}
              data-testid={`gov-connect-${c.id}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-xs font-bold text-white transition-all hover:border-purple-400/60 disabled:opacity-50">
              <Wallet className="h-3.5 w-3.5 text-purple-300" />
              {c.id === "injected" ? "MetaMask / Browser" : c.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-purple-400/30 bg-purple-400/[0.05] p-5" data-testid="gov-voting-power">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-purple-300/80">{t("yourVotingPower")}</p>
          <p className="mt-1 font-mono text-2xl font-black text-white" data-testid="gov-power-balance">
            {loadingPower ? <Loader2 className="inline h-5 w-5 animate-spin" /> : `${fmt(power?.balance_rbl ?? 0)} R₿L`}
          </p>
          {power && (
            <p className="mt-0.5 text-[11px] text-white/55">
              = {fmt(power.votes)} {t("votes")} · {power.supply_pct}% {t("ofSupply")}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="font-mono text-[10px] text-white/40">{address?.slice(0, 6)}…{address?.slice(-4)}</p>
          {power && (
            <div className="mt-1 space-y-0.5 text-[10px] font-mono">
              <p className={power.can_vote ? "text-emerald-300" : "text-white/35"}>
                {power.can_vote ? "✓" : "○"} {t("vote")} (≥{fmt(power.min_vote_rbl)})
              </p>
              <p className={power.can_propose ? "text-emerald-300" : "text-white/35"}>
                {power.can_propose ? "✓" : "○"} {t("createProposal")} (≥{fmt(power.min_propose_rbl)})
              </p>
            </div>
          )}
          <button onClick={() => disconnect()} data-testid="gov-disconnect"
            className="mt-2 rounded-full border border-white/15 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.18em] text-white/50 transition-colors hover:border-rose-400/40 hover:text-rose-300">
            {t("disconnect")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VotingPower;
